// ============================================
// RÉFÉRENTIEL — échelle Q-SORT (fit-culturel, plan §5.1)
// ============================================
// Module PUR : pas de `ctx`, pas de lecture base, unit-testable.
//
// Distribution FORCÉE : chaque niveau de la pyramide a une capacité fixe. Tous
// les répondants ont donc le MÊME total de points (mesure ipsative) — les
// vecteurs sont directement comparables sans normalisation. C'est la raison pour
// laquelle référents et candidat remplissent le *même* questionnaire : deux
// échelles différentes rendraient les écarts ininterprétables.

import type { DimensionReferentiel } from "./validation";

/** Une réponse Q-sort : un item placé à un niveau de la pyramide. */
export type ReponseQsort = { code: string; niveau: number };

/** Vecteur de scores par code de dimension. */
export type VecteurQsort = Record<string, number>;

/**
 * Score d'un niveau. Le niveau 0 est le PLUS FORT (haut de la pyramide) et vaut
 * `nbNiveaux` ; le dernier vaut 1. Aucun score nul : un item classé dernier
 * pèse quand même, l'écart avec l'avant-dernier reste de 1.
 */
export function scoreDuNiveau(niveau: number, nbNiveaux: number): number {
  return nbNiveaux - niveau;
}

/**
 * LA pyramide du Q-sort — unique et non paramétrable (§5.1).
 *
 * 23 items sur 7 niveaux (2-3-4-5-4-3-2) : les 23 items de la version courte
 * PCfit V2 remplissent exactement ses places. Sept crans donnent au classement
 * la finesse que cinq ne donnaient pas (deux paliers de plus entre l'essentiel
 * et le négligeable), le centre large absorbe les items « ni essentiels ni
 * négligeables », et les extrêmes étroits (2) forcent un vrai arbitrage.
 *
 * Une pyramide unique rend tous les référentiels fit-culturel comparables entre
 * campagnes : Dmax, score du hasard et taille des pôles de restitution sont les
 * mêmes partout. Générateur et presets multiples ont été retirés — une pyramide
 * par campagne signifiait des scores de matching non comparables d'une campagne
 * à l'autre, pour un réglage que personne n'avait de raison de faire.
 *
 * Les campagnes configurées AVANT ce figement portent l'ancienne pyramide
 * (2-3-5-3-2, 15 items) dans leur snapshot et restent relues telle quelle : tout
 * le moteur travaille sur la pyramide reçue, jamais sur cette constante. Seule
 * la validation d'une NOUVELLE config exige la pyramide ci-dessous.
 */
export const PYRAMIDE_FIT_CULTUREL: readonly number[] = [2, 3, 4, 5, 4, 3, 2];

/** Nombre d'items imposé par la pyramide (distribution forcée : somme exacte). */
export const NB_ITEMS_FIT_CULTUREL = PYRAMIDE_FIT_CULTUREL.reduce(
  (a, b) => a + b,
  0,
);

/** Copie mutable de la pyramide (les configs stockées portent un `number[]`). */
export function pyramideFitCulturel(): number[] {
  return [...PYRAMIDE_FIT_CULTUREL];
}

/** La pyramide fournie est-elle LA pyramide ? (invariant de config, §3.2) */
export function estPyramideFitCulturel(pyramide: number[]): boolean {
  return (
    pyramide.length === PYRAMIDE_FIT_CULTUREL.length &&
    pyramide.every((capacite, i) => capacite === PYRAMIDE_FIT_CULTUREL[i])
  );
}

/** Vecteur de scores complet induit par une pyramide (un score par item). */
export function scoresDeLaPyramide(pyramide: number[]): number[] {
  const nbNiveaux = pyramide.length;
  const scores: number[] = [];
  pyramide.forEach((capacite, niveau) => {
    for (let i = 0; i < capacite; i++) scores.push(scoreDuNiveau(niveau, nbNiveaux));
  });
  return scores;
}

/**
 * Le vecteur est-il un classement COMPLET en distribution forcée : un score par
 * dimension, et exactement les scores de la pyramide ? C'est ce que valide la
 * soumission (`assertContenuReferent`) ; l'agrégation le revérifie pour les
 * contributions plus anciennes, qu'elle relit sans contrôle de forme.
 */
export function estClassementForce(
  vecteur: VecteurQsort,
  codes: string[],
  pyramide: number[],
): boolean {
  if (Object.keys(vecteur).length !== codes.length) return false;
  if (codes.some((code) => vecteur[code] === undefined)) return false;
  const croissant = (a: number, b: number) => a - b;
  const attendus = scoresDeLaPyramide(pyramide).sort(croissant);
  const obtenus = codes.map((code) => vecteur[code]!).sort(croissant);
  return (
    attendus.length === obtenus.length &&
    attendus.every((score, i) => score === obtenus[i])
  );
}

/**
 * Distance L1 MAXIMALE entre deux classements de la même pyramide : on apparie
 * le vecteur trié croissant avec le même vecteur trié décroissant (le pire
 * appariement possible). Sert de dénominateur de normalisation.
 *
 * Pour `2-3-4-5-4-3-2` (23 items) → 64.
 */
export function distanceMax(pyramide: number[]): number {
  const croissant = scoresDeLaPyramide(pyramide).sort((a, b) => a - b);
  const decroissant = [...croissant].reverse();
  return croissant.reduce(
    (somme, s, i) => somme + Math.abs(s - decroissant[i]!),
    0,
  );
}

/**
 * Convertit des réponses Q-sort en vecteur de scores, en exigeant une réponse
 * par dimension : un vecteur incomplet fausserait silencieusement la distance
 * (une dimension absente compterait comme un écart nul).
 */
export function vecteurScores(
  reponses: ReponseQsort[],
  dimensions: Pick<DimensionReferentiel, "code">[],
  nbNiveaux: number,
): VecteurQsort {
  const parCode = new Map(reponses.map((r) => [r.code, r]));
  const vecteur: VecteurQsort = {};
  for (const { code } of dimensions) {
    const reponse = parCode.get(code);
    if (!reponse) {
      throw new Error(`vecteurScores: réponse manquante pour la dimension ${code}`);
    }
    vecteur[code] = scoreDuNiveau(reponse.niveau, nbNiveaux);
  }
  return vecteur;
}

/**
 * Proximité L1 ESPÉRÉE d'un appariement au hasard, pour une pyramide donnée —
 * repère de la proximité d'un référent au consensus (`divergenceQsort`). Le
 * matching d'un candidat, lui, est un r de Pearson, dont le hasard vaut 0.
 *
 * L'échelle L1 est tassée : deux classements indépendants du même jeu d'items
 * ne valent pas 0, ils valent typiquement 25-30 %. Sans ce repère, un 55 % se lit
 * « moyen » alors qu'il est excellent (§5.1) — la restitution doit donc SITUER la
 * proximité, jamais l'afficher brute seule.
 *
 * Exact, pas simulé. Pour une permutation uniforme π, chaque `t_π(i)` est
 * marginalement uniforme sur le multiensemble des scores, donc
 *
 *     E[Σ|sᵢ − t_π(i)|] = (1/n) · ΣᵢΣⱼ |sᵢ − sⱼ|
 *
 * ce qui se calcule en O(n²) sur quelques dizaines d'items.
 */
export function proximiteMoyenneAleatoire(pyramide: number[]): number {
  const scores = scoresDeLaPyramide(pyramide);
  const n = scores.length;
  const dmax = distanceMax(pyramide);
  if (n === 0 || dmax === 0) return 0;

  let sommeEcarts = 0;
  for (const a of scores) {
    for (const b of scores) sommeEcarts += Math.abs(a - b);
  }
  const distanceEsperee = sommeEcarts / n;
  return Math.max(0, Math.min(100, 100 * (1 - distanceEsperee / dmax)));
}
